// src/js/workouts.ts
//
// Phase 263 Plan 10 (CINDY-07/CINDY-08/CINDY-11) — the Workouts page module: session-list
// DataTable, the shared entry-form mount, and the save/delete AJAX wiring for /workouts/new and
// /workouts/<id>. Shape copied from src/js/runs.ts (263-PATTERNS.md's exact analog): a
// `static init = () => {...}` entry point plus one guarded function per page, each returning
// immediately when its own DOM anchor is absent — this is what makes ONE unconditional
// `workouts.init()` call (src/index.ts) safe on every page in the app, not a convenience.
//
// This module owns ZERO entry-form markup — src/js/cindy/entry-form.ts's mountCindyEntry() (263-05)
// is the only implementation of the date field, paste box, round-row repeater, leftover-reps,
// note and live readouts (P-18). Importing parseCindyNote/mountRoundEntry directly here would be
// a second, independently-drifting copy of that shared component — never done.
//
// Phase 264 Plan 06 (CINDY-01/CINDY-03/CINDY-04) adds liveTimer() below, a third guarded
// function alongside sessionList()/entryForm() — /workouts/live's [data-cindy-entry] mount lives
// inside live.latte's hidden `review` container, so entryForm()'s existing mount + bindSave()
// wiring IS the entire P-02 hand-off save path; liveTimer() only hands the already-mounted
// controller to the timer module. Namespace imports below (not named imports) are deliberate —
// see the liveTimer()/bindSave() call sites for why.

import DataTable from 'datatables.net';
import { Order } from 'datatables.net-bs5';
import { post, notifier } from './util';
import { mountCindyEntry, CindyEntrySession, CindyEntryController } from './cindy/entry-form';
import * as liveTimerModule from './cindy/live-timer';
import * as liveTimerStateModule from './cindy/live-timer-state';

export default class {
    static init = () => {
        sessionList();
        const controller = entryForm();
        liveTimer(controller);
    }
}

/** Local escape helper — same module-private copy shape as tracking.ts's escapeHtml() (no shared export exists in this repo to import instead). */
function escapeHtml(str: string): string {
    return str
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Formats a calendar-date-only string ('YYYY-MM-DD') as 'Mon D, Year' via pure string/number
 * arithmetic — deliberately never constructs a Date object or touches any timezone conversion.
 * P-11 / STATE.md's highest-priority standing constraint: a CALENDAR-DATE value must never pass
 * through localizeInstants()/Tz::toUtcIso() or anything instant-aware; this stays entirely inside
 * plain string parsing so there is no surface for that class of bug to even exist here.
 */
function formatMonthDayYear(dateStr: string): string {
    const parts = dateStr.split('-');
    const year = parts[0];
    const month = parseInt(parts[1], 10);
    const day = parseInt(parts[2], 10);
    return MONTH_NAMES[month - 1] + ' ' + day + ', ' + year;
}

/**
 * sessionList() — /workouts's DataTable, guarded on the shell UI-SPEC ships in
 * Workouts/default.latte (absent entirely on the zero-session empty-state branch, so this no-ops
 * cleanly there too, not just on unrelated pages).
 */
function sessionList(): void {
    const el = document.querySelector('.datatable[data-table="workouts"]');
    if (!el) return;

    new DataTable('.datatable[data-table="workouts"]', {
        pageLength: 25,
        processing: true,
        serverSide: true,
        ajax: {
            url: '?do=getSessions',
            data: function (data) {
                return data;
            }
        },
        columns: [
            // column.data === null -> the render callback's `data` argument IS the full row
            // object (CindyModel::getSessionsForList()'s shape: id/date/date_display/score/
            // total_seconds/total_display/note/rounds) — needed here because the cell links to
            // `row.id` while displaying `row.date_display`, two different row properties.
            {
                data: null,
                render: function (data: any, type: string) {
                    if (type !== 'display') return data.date_display;
                    return '<a href="/workouts/' + encodeURIComponent(String(data.id)) + '">' +
                        escapeHtml(data.date_display) + '</a>';
                }
            },
            {
                data: 'score',
                render: function (data: string, type: string) {
                    if (type !== 'display') return data;
                    // T-263-23b: escaped before concatenation — never a raw value in an HTML string.
                    return '<span class="cindy-score">' + escapeHtml(data) + '</span>';
                }
            },
            {
                data: 'total_display',
                render: function (data: string, type: string) {
                    if (type !== 'display') return data;
                    return '<span class="cindy-stat-value">' + escapeHtml(data) + '</span>';
                }
            },
            {
                // Note column — user-authored free text (T-263-23b's named XSS surface). Escaped
                // through this same render callback before ever touching an HTML string; the
                // null case renders a static '&mdash;' literal, never an interpolated value.
                // className mirrors the header's own d-none d-sm-table-cell (Workouts/default.latte)
                // onto the body cells DataTables generates — Bootstrap's breakpoint class only
                // hides the <th> on its own; the <td> needs the same class to match at 320px.
                data: 'note',
                className: 'd-none d-sm-table-cell',
                render: function (data: string | null, type: string) {
                    if (type !== 'display') return data ?? '';
                    if (!data) return '&mdash;';
                    // Inline max-width (not a new SCSS class): this plan's declared file scope is
                    // src/js/workouts.ts + src/index.ts only. Mirrors the actual, already-shipped
                    // truncation precedent for a list-cell in this codebase (Transactions/
                    // subscriptions.latte's style="max-width:200px" .text-truncate cells) rather
                    // than UI-SPEC's claim of an existing dedicated CSS class, which does not
                    // actually exist anywhere in style.scss for this shape.
                    return '<span class="text-muted text-truncate d-inline-block" style="max-width:220px">' +
                        escapeHtml(data) + '</span>';
                }
            }
        ],
        order: [[0, 'desc']] as Order[]
    });
}

/** Reads and JSON.parses [data-cindy-entry]'s data-session attribute — null/empty/unparseable -> create mode. */
function parseSessionAttribute(raw: string | undefined): CindyEntrySession | null {
    if (!raw) return null;
    try {
        const parsed = JSON.parse(raw);
        return parsed === null ? null : (parsed as CindyEntrySession);
    } catch (e) {
        return null;
    }
}

/**
 * entryForm() — mounts the shared lap-entry component into modules/cindy-entry-form.latte's one
 * <div data-cindy-entry> and wires the page's own Save/Delete buttons (siblings of that div in
 * Workouts/detail.latte, never rendered by this module or by the shared component itself).
 *
 * Returns the mounted controller (or null when [data-cindy-entry] is absent) — Phase 264 Plan 06
 * hands this to liveTimer() below, since live.latte's hidden `review` container ships the exact
 * same [data-cindy-entry]/.js-cindy-save-session markup Workouts/detail.latte does; this is the
 * entire reason the P-02 hand-off needs zero new save wiring of its own.
 */
function entryForm(): CindyEntryController | null {
    const rootEl = document.querySelector<HTMLElement>('[data-cindy-entry]');
    if (!rootEl) return null;

    const session = parseSessionAttribute(rootEl.dataset.session);
    const controller = mountCindyEntry(rootEl, session, { context: 'page' });

    const saveBtn = document.querySelector<HTMLButtonElement>('.js-cindy-save-session');
    if (saveBtn) {
        bindSave(saveBtn, controller);
    }

    // Delete only renders in edit mode (Workouts/detail.latte's {if $session} gate) — session is
    // guaranteed non-null here whenever the button actually exists in the DOM.
    if (session) {
        const deleteBtn = document.querySelector<HTMLButtonElement>('.js-cindy-delete-session');
        if (deleteBtn) {
            bindDelete(deleteBtn, session);
        }
    }

    return controller;
}

/**
 * liveTimer() — guarded init for /workouts/live's DOM-owning module (src/js/cindy/live-timer.ts,
 * Plan 264-06). Same discipline as sessionList()/entryForm() above: returns immediately when its
 * own DOM anchor (#cindy-live-timer) is absent, which is what makes ONE unconditional
 * workouts.init() call (src/index.ts, unchanged by this plan) safe on every page in the app —
 * including this one, where entryForm() ALSO mounts (see that function's own docblock). Namespace
 * import (liveTimerModule, top of file) rather than a named import so the call below reads as a
 * normal function call while keeping a single source-of-truth reference to the real exported
 * mount function — see key_links in 264-06-PLAN.md's frontmatter.
 */
function liveTimer(entryController: CindyEntryController | null): void {
    const rootEl = document.querySelector<HTMLElement>('#cindy-live-timer');
    if (!rootEl) return;
    liveTimerModule.mountLiveTimer(rootEl, { entryController });
}

/**
 * Array-of-pairs, not a plain object: URLSearchParams(plainObject) coerces an array-valued
 * property to ONE comma-joined string via String() rather than repeated `rounds[]=` entries,
 * which CindyModel::saveSession()'s `$post['rounds'] ?? []` cannot read back as a PHP array. A
 * sequence of [key, value] pairs is the URLSearchParams init form that supports a repeated key,
 * so post() (src/js/util.ts) can still be reused verbatim — no bespoke fetch wrapper needed.
 *
 * The three per-movement split POST arrays (264, CindyModel::saveSession()'s split arrays) are
 * emitted for EVERY index in `rounds[]` order — including indices with no splits, as an
 * empty-string entry — so they stay positionally aligned with `rounds[]` on the PHP side. A
 * sparse emission would silently shift every subsequent round's splits onto the wrong row.
 */
function buildSavePayload(payload: ReturnType<CindyEntryController['collectPayload']>): [string, string][] {
    const pairs: [string, string][] = [
        ['session_id', payload.session_id !== null ? String(payload.session_id) : ''],
        ['date', payload.date],
        ['rounds_completed', String(payload.rounds_completed)],
        ['partial_reps', String(payload.partial_reps)],
        ['note', payload.note],
    ];
    payload.rounds.forEach(function (duration) {
        pairs.push(['rounds[]', duration]);
    });
    payload.splits.forEach(function (splitTriple) {
        pairs.push(['pullup_splits[]', splitTriple ? splitTriple.pullups.toFixed(2) : '']);
        pairs.push(['pushup_splits[]', splitTriple ? splitTriple.pushups.toFixed(2) : '']);
        pairs.push(['squat_splits[]', splitTriple ? splitTriple.squats.toFixed(2) : '']);
    });
    return pairs;
}

function showInlineError(anchorEl: HTMLElement, message: string): void {
    clearInlineError(anchorEl);
    const el = document.createElement('div');
    // Same treatment entry-form.ts's own parse-error line uses (cindy-inline-error) — never a
    // silent failure, never an alert() box (Copywriting Contract / T-263-25 threat register).
    el.className = 'text-danger small mt-1 cindy-inline-error';
    el.textContent = message;
    anchorEl.insertAdjacentElement('afterend', el);
}

function clearInlineError(anchorEl: HTMLElement): void {
    const next = anchorEl.nextElementSibling;
    if (next && next.classList.contains('cindy-inline-error')) {
        next.remove();
    }
}

function bindSave(saveBtn: HTMLButtonElement, controller: CindyEntryController): void {
    saveBtn.addEventListener('click', function () {
        if (saveBtn.disabled) return; // re-entrancy guard — mirrors gym-modal's handleGymSaveAll (D-04)
        clearInlineError(saveBtn);

        const payload = controller.collectPayload();
        const isCreate = payload.session_id === null;

        saveBtn.disabled = true;

        // post() (src/js/util.ts) sets the 'X-Requested-With: XMLHttpRequest' header
        // unconditionally on every call it makes — the exact header
        // BasePresenter::processSignal()'s AJAX gate requires (T-263-02b). Reusing the shared
        // helper here means that header is never hand-duplicated (and never forgotten) at this
        // call site.
        post('?do=saveSession', buildSavePayload(payload), function (data: any) {
            // Re-enabled on BOTH the success and failure path — a dropped response must never
            // permanently disable the control (T-263-25, the 262-06 in-flight-lockout lesson).
            saveBtn.disabled = false;

            if (data.status === 1) {
                if (isCreate) {
                    // T-264-13: gated on #cindy-live-timer being present on THIS page, so a manual
                    // save from /workouts/new can never wipe a timer running in another tab — a
                    // saved session must never be offered for resume later (P-14).
                    if (document.querySelector('#cindy-live-timer')) {
                        try { liveTimerStateModule.clearPersistedState(window.localStorage); } catch { /* T-264-05: the window.localStorage property access itself can throw (Safari private browsing) */ }
                    }
                    window.location.href = '/workouts/' + data.session_id;
                    return;
                }
                // WorkoutsPresenter exposes no get-single-session signal to re-fetch from —
                // saveSession() itself returns only {status, session_id, rounds, score}. Refresh
                // in place from the just-submitted payload merged with the response's
                // session_id, never a full page reload (which would blow away focus/scroll).
                const refreshed: CindyEntrySession = {
                    id: data.session_id,
                    date: payload.date,
                    rounds_completed: payload.rounds_completed,
                    partial_reps: payload.partial_reps,
                    note: payload.note ? payload.note : null,
                    rounds: payload.rounds.map(function (duration, i) {
                        return { round_no: i + 1, duration_display: duration };
                    }),
                };
                controller.refresh(refreshed);
                notifier.success('Saved');
            } else {
                showInlineError(saveBtn, data.message || 'Save failed.');
            }
        });
    });
}

function bindDelete(deleteBtn: HTMLButtonElement, session: CindyEntrySession): void {
    deleteBtn.addEventListener('click', function () {
        if (deleteBtn.disabled) return;

        const sessionIdAttr = deleteBtn.dataset.sessionId;
        const sessionId = sessionIdAttr ? parseInt(sessionIdAttr, 10) : session.id;
        if (!sessionId) return;

        const dateDisplay = formatMonthDayYear(session.date);
        const roundCount = session.rounds.length;
        // UI-SPEC's exact Copywriting Contract copy — 'Delete this session?' title,
        // 'This removes {Mon D, Year}'s session and all {N} round times. This cannot be undone.' body.
        const confirmed = window.confirm(
            'Delete this session?\n\nThis removes ' + dateDisplay + "'s session and all " +
            roundCount + ' round times. This cannot be undone.'
        );
        if (!confirmed) return;

        deleteBtn.disabled = true;

        // post() (src/js/util.ts) — same shared helper, same unconditional
        // 'X-Requested-With: XMLHttpRequest' header as bindSave() above.
        post('?do=deleteSession', { session_id: sessionId }, function (data: any) {
            deleteBtn.disabled = false;

            if (data.status === 1) {
                window.location.href = '/workouts';
            } else {
                showInlineError(deleteBtn, data.message || 'Delete failed.');
            }
        });
    });
}
